import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Settings as SettingsIcon, AlertTriangle, Trash2, RefreshCw, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, auth } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const AI_MODELS = [
  { value: "deepseek/deepseek-chat-v3.1", label: "DeepSeek Chat v3.1" },
  { value: "deepseek/deepseek-chat", label: "DeepSeek Chat" },
  { value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
  { value: "anthropic/claude-3-haiku", label: "Claude 3 Haiku" },
  { value: "openai/gpt-4o", label: "GPT-4o" },
  { value: "openai/gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "openai/gpt-4-turbo", label: "GPT-4 Turbo" },
  { value: "google/gemini-pro-1.5", label: "Gemini Pro 1.5" },
  { value: "meta-llama/llama-3.1-70b-instruct", label: "Llama 3.1 70B" },
  { value: "mistralai/mixtral-8x7b-instruct", label: "Mixtral 8x7B" },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    ai_model: "deepseek/deepseek-chat-v3.1",
    has_api_key: false,
  });
  const [apiKey, setApiKey] = useState("");
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  // Audit Logs State
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPassword, setAuditPassword] = useState("");

  const user = auth.getUser();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        ai_model: settings.ai_model,
      };
      if (apiKey) {
        updateData.openrouter_api_key = apiKey;
      }
      await api.updateSettings(updateData);
      toast.success("Settings saved");
      setApiKey("");
      loadSettings();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.getErrorLogs();
      setLogs(res.data);
    } catch (error) {
      console.error("Failed to fetch logs", error);
      toast.error("Failed to fetch logs");
    }
  };

  const clearLogs = async () => {
    if (!window.confirm("Are you sure you want to clear all system logs?")) return;
    try {
      await api.clearErrorLogs();
      setLogs([]);
      toast.success("Logs cleared");
    } catch (error) {
      console.error("Failed to clear logs", error);
      toast.error("Failed to clear logs");
    }
  };

  const handleAuditLogin = async () => {
    if (auditPassword !== "admin123") {
      toast.error("Invalid password");
      return;
    }

    try {
      // Use password in headers/query if needed or just unlock UI
      // For now we unlock UI and fetch. Backend protected by role, UI protected by password.
      const res = await api.getAuditLogs(auditPassword);
      setAuditLogs(res.data);
      setShowAuditLogs(true);
      toast.success("Access granted");
    } catch (error) {
      console.error("Failed to fetch audit logs", error);
      toast.error("Failed to fetch logs or access denied");
    }
  };

  useEffect(() => {
    if (showLogs) {
      fetchLogs();
    }
  }, [showLogs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="back-to-dashboard">
              <Button variant="ghost" size="sm" className="rounded-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="settings-title">
                AI Settings
              </h1>
              <p className="text-sm text-slate-500">Configure AI model and API keys</p>
            </div>
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* OpenRouter API Key */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              OpenRouter Configuration
            </CardTitle>
            <CardDescription>
              Configure your OpenRouter API key for AI analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="api_key" className="text-slate-700">
                API Key {settings.has_api_key && <span className="text-emerald-600">(Configured)</span>}
              </Label>
              <Input
                id="api_key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={settings.has_api_key ? "Enter new key to update..." : "sk-or-v1-..."}
                className="mt-1.5 h-11 rounded-sm border-slate-300"
                data-testid="api-key-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Get your API key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">openrouter.ai/keys</a>
              </p>
            </div>

            <div>
              <Label htmlFor="ai_model" className="text-slate-700">AI Model</Label>
              <Select
                value={settings.ai_model}
                onValueChange={(value) => setSettings({ ...settings, ai_model: value })}
              >
                <SelectTrigger
                  className="mt-1.5 h-11 rounded-sm border-slate-300"
                  data-testid="ai-model-select"
                >
                  <SelectValue placeholder="Select AI model" />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((model) => (
                    <SelectItem key={model.value} value={model.value}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Model used for transcript analysis and scoring
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Current User Info */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader>
            <CardTitle className="text-base font-['Manrope']">Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Name:</span>
                <span className="ml-2 font-medium text-slate-900">{user?.name}</span>
              </div>
              <div>
                <span className="text-slate-500">Email:</span>
                <span className="ml-2 font-medium text-slate-900">{user?.email}</span>
              </div>
              <div>
                <span className="text-slate-500">Role:</span>
                <span className="ml-2 font-medium text-slate-900 capitalize">
                  {(user?.role === "user" || user?.role === "caseworker") ? "case worker" : user?.role?.replace("_", " ")}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Logs (Super Admin Only) */}
        <Card className="border-red-200 bg-red-50/10 rounded-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Manrope'] flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                System Error Logs
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLogs(!showLogs)}
                className="bg-white border-red-200 hover:bg-red-50 text-red-700"
              >
                {showLogs ? "Hide Logs" : "View Logs"}
              </Button>
            </div>
            <CardDescription className="text-red-700/70">
              Technical logs for debugging. Only accessible to Super Admin.
            </CardDescription>
          </CardHeader>
          {showLogs && (
            <CardContent>
              <div className="flex justify-end gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={fetchLogs} className="bg-white">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="destructive" size="sm" onClick={clearLogs}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Logs
                </Button>
              </div>
              <div className="border border-red-200 rounded-md bg-white max-h-[400px] overflow-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 border-b sticky top-0">
                    <tr>
                      <th className="px-4 py-2 font-medium text-slate-500 w-40">Time</th>
                      <th className="px-4 py-2 font-medium text-slate-500 w-20">Level</th>
                      <th className="px-4 py-2 font-medium text-slate-500">Message</th>
                      <th className="px-4 py-2 font-medium text-slate-500 w-40">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No logs found</td></tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-slate-50 last:border-0">
                          <td className="px-4 py-2 whitespace-nowrap text-slate-400 font-mono text-xs">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.level === 'ERROR' ? 'bg-red-100 text-red-700' :
                              log.level === 'WARNING' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                              {log.level}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-700 max-w-xl truncate" title={log.message}>
                            {log.message}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500 font-mono">
                            {log.module}:{log.line}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Audit Logs (Password Protected) */}
        <Card className="border-indigo-200 bg-indigo-50/10 rounded-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Manrope'] flex items-center gap-2 text-indigo-800">
                <FileText className="w-5 h-5" />
                Audit Logs
              </CardTitle>
            </div>
            <CardDescription className="text-indigo-700/70">
              View system access and activity logs. Protected area.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showAuditLogs ? (
              <div className="flex flex-col items-center gap-4 py-6 border border-indigo-100 rounded-md bg-white/50">
                <p className="text-sm text-indigo-600 font-medium">Enter admin password to view logs</p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="Password"
                    value={auditPassword}
                    onChange={(e) => setAuditPassword(e.target.value)}
                    className="w-48 h-9 border-indigo-200"
                    onKeyDown={(e) => e.key === "Enter" && handleAuditLogin()}
                  />
                  <Button size="sm" onClick={handleAuditLogin} className="bg-indigo-600 hover:bg-indigo-700 h-9">
                    Unlock
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold text-indigo-900">Activity History</h4>
                  <Button variant="ghost" size="sm" onClick={() => setShowAuditLogs(false)} className="text-slate-500 h-8">
                    Lock
                  </Button>
                </div>

                <div className="border border-indigo-100 rounded-md bg-white max-h-[500px] overflow-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b sticky top-0">
                      <tr>
                        <th className="px-4 py-2 font-medium text-slate-500 w-40">Time (UTC)</th>
                        <th className="px-4 py-2 font-medium text-slate-500">Action</th>
                        <th className="px-4 py-2 font-medium text-slate-500">User</th>
                        <th className="px-4 py-2 font-medium text-slate-500">IP</th>
                        <th className="px-4 py-2 font-medium text-slate-500">Path</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No activity recorded</td></tr>
                      ) : (
                        auditLogs.map((log, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50 last:border-0 border-slate-100">
                            <td className="px-4 py-2 whitespace-nowrap text-slate-500 font-mono text-xs">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 font-medium text-slate-700">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${log.action === 'login' ? 'bg-green-100 text-green-700' :
                                log.action === 'page_view' ? 'bg-blue-50 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                {log.action}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-600">
                              {log.user_email}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-slate-500">
                              {log.ip_address || "-"}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-slate-500 truncate max-w-[200px]" title={log.details?.path}>
                              {log.details?.path || "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-slate-700 hover:bg-slate-800 rounded-sm px-6"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </main>
    </div >
  );
}
